import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import MesaDieciocho from "./MesaDieciocho";

// "El Dieciocho, también de turno": explica la fecha a quien no es de Chile y
// reconoce lo que significa trabajarla.
//
// Cómo se abre: SOLO porque la persona tocó el enlace. No interrumpe nada, no
// es un popup, no abre el chat de Benito y no vive en ninguna pantalla
// clínica. Es un velo por encima, así que al cerrarlo la persona vuelve
// exactamente a donde estaba, con su trabajo intacto.
//
// Hoja desde abajo en el teléfono, diálogo centrado en escritorio (el patrón
// de RachaSheet y DetalleDiaSheet, más la variante `sm:` que el shell de
// escritorio pedía). Se cierra por la X, por Escape, tocando el velo o
// arrastrando la hoja hacia abajo: cuatro salidas, todas visibles o conocidas.
//
// El contenido es texto de fecha y NO toca evoluciones, entregas ni documentos
// clínicos: vive entero en este archivo.
//
// Criterios de copy de la ola, por si alguien viene a editar: nada de héroes,
// ángeles, vocación sin descanso ni sacrificio como deber; no se afirma que
// todo el mundo esté celebrando (hay gente sola, lejos de casa o que no
// celebra); no se vende ni se invita a registrarse; sin dos puntos, sin
// guiones largos y sin emojis.
export default function PanelDieciocho({ abierto, onCerrar }) {
  const inicioToqueY = useRef(null);
  const cerrarRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const alTeclear = (e) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [abierto, onCerrar]);

  useEffect(() => {
    document.body.style.overflow = abierto ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [abierto]);

  // El foco entra al panel al abrir y VUELVE a donde estaba al cerrar: sin lo
  // segundo, quien navega con teclado queda tirado en <body> y tiene que
  // recorrer la página entera de nuevo.
  useEffect(() => {
    if (!abierto) return undefined;
    const previo = document.activeElement;
    cerrarRef.current?.focus();
    return () => { if (previo && previo.focus) previo.focus(); };
  }, [abierto]);

  // Trampa de foco: `aria-modal` confina al lector de pantalla pero NO al Tab
  // del teclado, así que sin esto un Tab se escapa al contenido que quedó
  // tapado por el velo.
  useEffect(() => {
    if (!abierto) return undefined;
    const alTabular = (e) => {
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focos = panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (!focos.length) return;
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener("keydown", alTabular);
    return () => document.removeEventListener("keydown", alTabular);
  }, [abierto]);

  if (!abierto) return null;

  // El arrastre para cerrar SOLO cuenta si la hoja ya está arriba del todo.
  // Los handlers viven en el mismo div que scrollea, así que sin esta guarda
  // bajar el dedo para volver atrás en el texto cerraba el panel a media
  // lectura, y este panel scrollea siempre en un teléfono.
  const alEmpezarToque = (e) => {
    inicioToqueY.current = e.currentTarget.scrollTop <= 0 ? e.touches[0].clientY : null;
  };
  const alSoltarToque = (e) => {
    if (inicioToqueY.current === null) return;
    if (e.currentTarget.scrollTop <= 0 && e.changedTouches[0].clientY - inicioToqueY.current > 60) onCerrar();
    inicioToqueY.current = null;
  };

  return (
    <>
      <div
        className="dieciocho-velo fixed inset-0 bg-black/40 z-40"
        onClick={onCerrar}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dieciocho-titulo"
        data-testid="panel-dieciocho"
        className={
          "dieciocho-panel fixed z-50 bg-paper overflow-y-auto "
          // Teléfono: hoja pegada abajo. Escritorio: tarjeta centrada.
          + "inset-x-0 bottom-0 rounded-t-[20px] max-h-[88vh] "
          + "sm:inset-0 sm:m-auto sm:h-fit sm:max-w-xl sm:max-h-[84vh] sm:rounded-tk-lg sm:border sm:border-edge"
        }
        style={{ boxShadow: "0 -4px 32px rgba(0,0,0,0.12)" }}
        onTouchStart={alEmpezarToque}
        onTouchEnd={alSoltarToque}
      >
        {/* El tirador de la hoja, solo en teléfono. */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
          <div className="w-9 h-1 rounded-full bg-edge" />
        </div>

        {/* El cierre va VISIBLE y pegado arriba mientras se lee, porque el
            texto es largo y no se puede pedir que vuelva al principio. */}
        <div className="sticky top-0 z-10 flex justify-end bg-paper/95 backdrop-blur-sm px-3 pt-2 pb-1">
          <button
            ref={cerrarRef}
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-full text-ink-subtle hover:text-ink hover:bg-surface transition-colors"
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        {/* text-left explícito: este panel se monta dentro del bloque
            centrado de la landing y heredaba el centrado. Un párrafo
            largo centrado no se lee. */}
        <div className="px-6 pb-10 sm:px-9 text-left">
          <MesaDieciocho className="mx-auto max-w-[320px] mb-6" />

          <h2 id="dieciocho-titulo" className="font-serif text-h2 text-ink leading-tight">
            El Dieciocho, también de turno
          </h2>

          {/* Editorial: párrafos que respiran, sin una caja por bloque. Los
              títulos de sección son la única jerarquía. */}
          <p className="mt-4 text-body font-sans text-ink-subtle leading-relaxed">
            Estos días celebramos las Fiestas Patrias en Chile. Si viste que MiPAE tiene un poco más
            de azul, blanco y rojo, es por eso!
          </p>

          <h3 className="mt-8 font-serif text-h3 text-ink">Qué celebramos</h3>
          <p className="mt-2 text-body font-sans text-ink-subtle leading-relaxed">
            Le decimos “el Dieciocho” a las celebraciones del 18 de septiembre. Recordamos la
            Primera Junta Nacional de Gobierno de 1810, parte del proceso que llevó a la
            independencia de Chile, proclamada en 1818.
          </p>

          <h3 className="mt-8 font-serif text-h3 text-ink">Cómo se vive</h3>
          <p className="mt-2 text-body font-sans text-ink-subtle leading-relaxed">
            Para muchas personas son días de reunirse con la familia y los amigos, compartir
            empanadas o un asado y alargar la sobremesa. También están las fondas, espacios de
            celebración con comida, música y bailes como la cueca.
          </p>

          <h3 className="mt-8 font-serif text-h3 text-ink">Cuando toca trabajar</h3>
          <p className="mt-2 text-body font-sans text-ink-subtle leading-relaxed">
            En salud, estas fechas también se pasan de turno. A veces cuesta salir de la casa cuando
            recién están poniendo la mesa, o ver las fotos de la familia mientras uno sigue
            trabajando.
          </p>
          {/* La frase que carga el peso de la pieza va destacada, con la voz
              que enseña del design system (DxClause): serif en itálica. */}
          <p className="mt-4 font-serif italic text-reasoning text-ink leading-relaxed">
            Podemos querer mucho lo que hacemos y aun así querer estar ahí. Las dos cosas caben.
          </p>
          <p className="mt-4 text-body font-sans text-ink-subtle leading-relaxed">
            Si hoy te tocó, ojalá puedas compartir algo rico con tus compañeros y tengas otro momento
            para reunirte con tu gente. Y si este año estás lejos de tu país, un saludo para ti
            también, que compartes el turno y estas costumbres con nosotros.
          </p>

          {/* El cierre se separa con una regla fina, no con otra caja. */}
          <hr className="mt-9 border-edge" />
          <p className="mt-5 text-body font-sans text-ink-subtle leading-relaxed">
            MiPAE nació en Chile, de la mano de un enfermero. Por eso quisimos compartir un poquito
            de estas fechas contigo, estés donde estés.
          </p>
          <p className="mt-3 text-body font-sans text-ink font-semibold">
            Felices Fiestas Patrias!
          </p>
        </div>
      </div>
    </>
  );
}
